import type {
  AiAllocationAction,
  ConversationTurn,
  LessonPlanningCell,
} from '../types/teacherAllocationAi'
import type {ProposalChange} from './teacherAllocationModel'

/**
 * Persisted state for Versi, the Teacher Allocation assistant.
 *
 * Step4Manual unmounts whenever the user navigates to another wizard step, which would otherwise
 * drop the conversation and a pending proposal (handover 4C: chat history and pending proposals
 * are retained while the user visits the grid or the allocation methods). Persisting them lets the
 * user step away and come back.
 *
 * sessionStorage, not localStorage, on purpose: logging out runs `localStorage.clear()` in the user
 * reducer, so a localStorage copy would be wiped. sessionStorage survives both step navigation and
 * logout -> login within the same tab.
 */

/** One entry of the conversation as the page shows it. */
export type VersiItem =
  | {id: string; kind: 'user'; text: string}
  | {id: string; kind: 'assistant'; text: string}
  | {
      id: string
      kind: 'proposal'
      message: string
      changes: ProposalChange[]
      warnings: string[]
      status: 'pending' | 'applied' | 'discarded' | 'superseded'
      /** Server flow: Versi's own summary (with the server's notes), shown as the message. */
      text?: string
      /** Server flow: the proposal's id on the server. */
      proposalId?: string
      /** Server flow: progress if applied, as the server projected it. */
      projected?: {filled: number; total: number}
      /** Server flow: progress now, shown beside the projection ("18 → 19 of 40"). */
      saved?: {filled: number; total: number}
    }
  | {
      id: string
      kind: 'clarify'
      question: string
      options: Array<{
        label: string
        employeeId: string
        matchedName: string
        /** Server flow: what the server acts on (`teacher:22`, `branch:all`, …). */
        value?: string
        /** Server flow: switch the Branch / Level dropdowns to this first. */
        scope?: {branchId?: number | null; levelId?: number | null}
      }>
      answered: boolean
    }
  | {
      id: string
      kind: 'result'
      applied: number
      filled: number
      total: number
      undoId: string
      undone: boolean
      /** Only frequency / consecutive periods changed: the result leaves out the filled count. */
      settingsOnly?: boolean
    }
  | {
      id: string
      kind: 'notice'
      tone: 'info' | 'warning' | 'error'
      textKey: string
      /** Server flow: the last message failed to send; Retry resends it. */
      retry?: boolean
    }

/** What an Apply changed, slot by slot, so Undo can put exactly those slots back. */
export type VersiUndoEntry = Record<string, {cell: LessonPlanningCell | null; deleted: boolean}>

/** The proposal waiting for approval — what Apply needs besides what is on screen. */
export type VersiPendingProposal = {
  itemId: string
  prompt: string
  actions: AiAllocationAction[]
  /** The backend asked for a confirmed second call (overwrites); Apply makes it. */
  needsConfirmCall: boolean
  /** The affected slots when the proposal was made (see proposalIsStale). */
  snapshot: Record<string, string>
}

export type PersistedVersiSession = {
  version: number
  conversationHistory: ConversationTurn[]
  items: VersiItem[]
  pending: VersiPendingProposal | null
  undo: Record<string, VersiUndoEntry>
  examplesCollapsed: boolean
}

// Own prefix: the legacy floating assistant (useTeacherAllocationAi) keeps its session under
// `onboardingTeacherAllocationAi`, and the two must never read each other's shape.
const STORAGE_KEY_PREFIX = 'onboardingVersiAllocation'
const SCHEMA_VERSION = 1

/** Only the latest applies can be undone; older snapshots are dropped oldest-first. */
export const MAX_UNDO_ENTRIES = 10

/** Scope the key per user so a different login in the same tab never inherits a session. */
export function buildVersiSessionKey(urddId?: string | null, tenant?: string | null) {
  return `${STORAGE_KEY_PREFIX}:${tenant || 'default'}:${urddId || 'anon'}`
}

const isConversationTurn = (value: any): value is ConversationTurn =>
  value &&
  typeof value === 'object' &&
  (value.role === 'user' || value.role === 'assistant') &&
  typeof value.content === 'string'

const isItem = (value: any): value is VersiItem =>
  value &&
  typeof value === 'object' &&
  typeof value.id === 'string' &&
  typeof value.kind === 'string'

export function readVersiSession(key: string): PersistedVersiSession | null {
  try {
    const raw = sessionStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    // Drop anything written by an older shape rather than half-restoring it.
    if (!parsed || typeof parsed !== 'object' || parsed.version !== SCHEMA_VERSION) return null
    const pending =
      parsed.pending && typeof parsed.pending === 'object' && Array.isArray(parsed.pending.actions)
        ? (parsed.pending as VersiPendingProposal)
        : null
    return {
      version: SCHEMA_VERSION,
      conversationHistory: Array.isArray(parsed.conversationHistory)
        ? parsed.conversationHistory.filter(isConversationTurn)
        : [],
      items: Array.isArray(parsed.items) ? parsed.items.filter(isItem) : [],
      pending,
      undo: parsed.undo && typeof parsed.undo === 'object' ? parsed.undo : {},
      examplesCollapsed: Boolean(parsed.examplesCollapsed),
    }
  } catch {
    // ignore storage / parse errors
    return null
  }
}

export function writeVersiSession(key: string, session: Omit<PersistedVersiSession, 'version'>) {
  const undoIds = Object.keys(session.undo)
  // Retry with fewer undo entries so a quota error costs undo depth, not the conversation.
  for (let keep = Math.min(undoIds.length, MAX_UNDO_ENTRIES); keep >= 0; keep--) {
    try {
      const kept = undoIds.slice(undoIds.length - keep)
      sessionStorage.setItem(
        key,
        JSON.stringify({
          ...session,
          version: SCHEMA_VERSION,
          undo: Object.fromEntries(kept.map((id) => [id, session.undo[id]])),
        })
      )
      return
    } catch {
      // quota exceeded (or storage unavailable) - shed an entry and try again
    }
  }
}

/**
 * Server flow: the server keeps the conversation, but after a reopen it only knows the pending
 * proposal's content — an applied, discarded or superseded proposal comes back as plain text and
 * the result under it (View updated grid / Undo) is gone. Its cards are kept here by proposal id
 * so leaving the assistant (landing, grid, another step) and coming back shows them as they were.
 */
type ProposalCard = Extract<VersiItem, {kind: 'proposal'}>
type ResultCard = Extract<VersiItem, {kind: 'result'}>
export type VersiServerCards = Record<string, {proposal: ProposalCard; result?: ResultCard}>

const SERVER_CARDS_KEY = `${STORAGE_KEY_PREFIX}:serverCards`
/** Oldest proposals are dropped first. */
const MAX_SERVER_CARDS = 30

export function readVersiServerCards(): VersiServerCards {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(SERVER_CARDS_KEY) ?? 'null')
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

/** Records the proposal cards in `items` (each with the result shown under it), over the stored ones. */
export function writeVersiServerCards(items: VersiItem[]) {
  const next: VersiServerCards = {}
  let current: string | null = null
  items.forEach((item) => {
    if (item.kind === 'proposal' && item.proposalId) {
      current = item.proposalId
      next[current] = {proposal: item}
    } else if (item.kind === 'result' && current) {
      next[current] = {...next[current], result: item}
    }
  })
  if (Object.keys(next).length === 0) return
  const stored = readVersiServerCards()
  Object.keys(next).forEach((id) => delete stored[id])
  const merged = {...stored, ...next}
  const ids = Object.keys(merged).slice(-MAX_SERVER_CARDS)
  try {
    sessionStorage.setItem(
      SERVER_CARDS_KEY,
      JSON.stringify(Object.fromEntries(ids.map((id) => [id, merged[id]])))
    )
  } catch {
    // ignore storage errors: the cards fall back to Versi's text
  }
}

export function clearVersiServerCards() {
  try {
    sessionStorage.removeItem(SERVER_CARDS_KEY)
  } catch {
    // ignore storage errors
  }
}

export function clearVersiSession(key: string) {
  try {
    sessionStorage.removeItem(key)
  } catch {
    // ignore storage errors
  }
}
